import React, { createContext, useCallback, useContext, useRef, useState } from 'react';

// Drop-in replacement for window.confirm / window.alert that uses an in-app
// modal so the UX stays consistent with the rest of the system, supports RTL
// Hebrew layout, and lets us style destructive actions distinctively.
//
//   const confirm = useConfirm();
//   const ok = await confirm({ title: 'מחיקת קמפיין', message: '...', danger: true });
//   if (!ok) return;
//
// `alert(message)` is also exposed for one-line info popups.

export interface ConfirmOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  /** Renders the confirm button in red — use for delete/irreversible flows. */
  danger?: boolean;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;
type AlertFn = (message: string, title?: string) => Promise<void>;

interface DialogCtx {
  confirm: ConfirmFn;
  alert: AlertFn;
}

const Ctx = createContext<DialogCtx | null>(null);

export function useConfirm(): ConfirmFn {
  const c = useContext(Ctx);
  if (!c) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return c.confirm;
}

export function useAlert(): AlertFn {
  const c = useContext(Ctx);
  if (!c) throw new Error('useAlert must be used inside <ConfirmProvider>');
  return c.alert;
}

interface DialogState extends ConfirmOptions {
  /** When true, show only the OK button (used by `alert`). */
  alertOnly?: boolean;
  resolve: (v: boolean) => void;
}

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<DialogState | null>(null);
  // Keep a ref so the dispatcher functions remain stable across renders —
  // re-creating `confirm`/`alert` on every render would break referential
  // equality for downstream useEffect/useCallback deps.
  const stateRef = useRef(setState);
  stateRef.current = setState;

  const confirm: ConfirmFn = useCallback((opts) => {
    return new Promise<boolean>(resolve => {
      stateRef.current({ ...opts, resolve });
    });
  }, []);

  const alert: AlertFn = useCallback((message, title) => {
    return new Promise<void>(resolve => {
      stateRef.current({ message, title, alertOnly: true, resolve: () => resolve() });
    });
  }, []);

  const close = (value: boolean) => {
    if (!state) return;
    state.resolve(value);
    setState(null);
  };

  return (
    <Ctx.Provider value={{ confirm, alert }}>
      {children}
      {state && (
        <div
          onClick={() => close(false)}
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(15,23,42,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            dir="rtl"
            style={{
              background: '#fff', borderRadius: 14, width: '100%', maxWidth: 440,
              boxShadow: '0 20px 50px rgba(0,0,0,0.30)',
              padding: 22,
              fontFamily: 'inherit',
            }}
          >
            {state.title && (
              <h3 style={{
                margin: '0 0 10px', fontSize: 17, fontWeight: 800,
                color: state.danger ? '#b91c1c' : '#111827',
              }}>
                {state.title}
              </h3>
            )}
            <p style={{ margin: 0, fontSize: 14, color: '#374151', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
              {state.message}
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-start', marginTop: 20 }}>
              <button
                autoFocus
                onClick={() => close(true)}
                style={{
                  background: state.danger ? '#dc2626' : '#2563eb',
                  color: '#fff', border: 'none', borderRadius: 8,
                  padding: '8px 20px', fontWeight: 700, fontSize: 14, cursor: 'pointer',
                }}
              >
                {state.confirmText || (state.alertOnly ? 'אישור' : (state.danger ? 'מחק' : 'אישור'))}
              </button>
              {!state.alertOnly && (
                <button
                  onClick={() => close(false)}
                  style={{
                    background: '#fff', color: '#374151',
                    border: '1px solid #d1d5db', borderRadius: 8,
                    padding: '8px 20px', fontWeight: 600, fontSize: 14, cursor: 'pointer',
                  }}
                >
                  {state.cancelText || 'ביטול'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </Ctx.Provider>
  );
};
